import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

type ChatLayout = "modal" | "panel";

const LAYOUT_KEY = "nlp_chat_layout";

function getStoredLayout(): ChatLayout {
  const stored = localStorage.getItem(LAYOUT_KEY);
  return stored === "panel" ? "panel" : "modal";
}

interface NLPChatContextValue {
  isOpen: boolean;
  layout: ChatLayout;
  /** `question` seeds the input, so a hand-off carries what was already typed. */
  openChat: (question?: string) => void;
  closeChat: () => void;
  toggleLayout: () => void;
  /** True when the panel is visible and occupying space */
  isPanelVisible: boolean;
  /** Question handed over by another surface, read once by the chat input. */
  pendingQuestion: string | null;
  consumePendingQuestion: () => void;
}

const NLPChatContext = createContext<NLPChatContextValue | null>(null);

export function NLPChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState<ChatLayout>(getStoredLayout);

  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

  const openChat = useCallback((question?: string) => {
    if (question) setPendingQuestion(question);
    setIsOpen(true);
  }, []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const consumePendingQuestion = useCallback(() => setPendingQuestion(null), []);

  const toggleLayout = useCallback(() => {
    setLayout((prev) => {
      const next = prev === "modal" ? "panel" : "modal";
      localStorage.setItem(LAYOUT_KEY, next);
      return next;
    });
  }, []);

  const isPanelVisible = isOpen && layout === "panel";

  return (
    <NLPChatContext.Provider
      value={{
        isOpen,
        layout,
        openChat,
        closeChat,
        toggleLayout,
        isPanelVisible,
        pendingQuestion,
        consumePendingQuestion,
      }}
    >
      {children}
    </NLPChatContext.Provider>
  );
}

export function useNLPChat(): NLPChatContextValue {
  const ctx = useContext(NLPChatContext);
  if (!ctx) throw new Error("useNLPChat must be used within NLPChatProvider");
  return ctx;
}
